/**
 * Plot runtime state. This is the non-reactive half of the
 * blob's `plot` object - the live uPlot instance and the caches it needs across
 * redraws (last data, legs, changeovers, the drawn x-extent, the drag-zoom
 * debounce, the leg-search cache, ETA timing samples). None of this serializes
 * to the URL and no component reads it directly, so it deliberately stays out of
 * the atom store; the *records* a component must mirror are atoms in
 * `plot-ui-state.ts` instead.
 *
 * `<plot-chart>` hands its DOM nodes here via `attachDom` on mount, so the
 * imperative renderer never reaches into `document` for the chart container.
 */

import type uPlot from 'uplot';
import type { AnyPlotResponse, Changeover, Leg, PlotResponse } from '../../api/types.ts';
import type { ShipPlotResult } from './multi-ship-plot.ts';
import type { PlotPanelResult } from './period-plot.ts';
import { lastEstimate } from '../../state/store.ts';
import { plotXWindow, changeoverLegend, chartReady, chartEmpty, DEFAULT_EMPTY } from './plot-ui-state.ts';

export interface XExtent {
  min: number;
  max: number;
}

interface PlotRuntime {
  instance: uPlot | null;
  /**
   * Grid view's uPlot instances, one per ship panel. Kept apart from `instance`
   * so the single-chart path (single ship and overlay) keeps its singleton and
   * every hook that reads `runtime.instance` behaves exactly as before.
   */
  gridInstances: uPlot[];
  /** Number of visible slots; includes empty/error comparison panels. */
  gridPanelCount: number;
  isFetching: boolean;
  /** Set around `new uPlot()` so the synchronous setScale hook doesn't replot. */
  inRebuild: boolean;
  /**
   * The clock a live chart follows, epoch seconds, or null when it is not
   * following (`live-tick.ts`). Its draw hook places the NOW line from this.
   */
  liveNowS: number | null;
  /** A Plot was requested mid-fetch; re-run once the current one settles. */
  pendingReplot: boolean;
  resizeObs: ResizeObserver | null;
  /** Watches the grid host; grid owns its own so chart-dom need not know it. */
  gridResizeObs: ResizeObserver | null;

  lastData: AnyPlotResponse | null;
  /**
   * Per-ship responses behind a multi-ship draw, null for a single ship. The
   * overlay merges these into `lastData`; grid mode will render them side by
   * side instead.
   */
  lastByShip: ShipPlotResult[] | null;
  /** Same-ship comparison results, keyed by stable panel identity rather than ship. */
  lastByPeriod: PlotPanelResult[] | null;
  /**
   * What a slicer move or a Play frame needs to redraw the single-ship scatter
   * in place: the instance, the unsliced response it was drawn from, and each
   * series' unsliced values. Null for any other chart, which rebuilds instead.
   */
  liveSlice: {
    instance: uPlot;
    source: PlotResponse;
    originals: Array<Array<number | null>>;
  } | null;
  /** Full-range immutable responses restored by Reset after comparison refinement. */
  periodBaseResults: PlotPanelResult[] | null;
  /** Invalidates late comparison responses after a newer plot/mode transition. */
  comparisonGeneration: number;
  /** Active elapsed display window; base period dates remain immutable. */
  periodViewWindow: XExtent | null;
  retryPeriodPanel: ((panelId: string) => void) | null;
  lastLegs: Leg[] | null;
  lastChangeovers: Changeover[] | null;
  /**
   * Grid view's overlays, keyed by ship. Leg and changeover bands are per-ship,
   * so a grid panel draws its own ship's - which is the whole reason these are
   * offered in grid and not on a shared overlay.
   */
  legsByShip: Record<string, Leg[]>;
  changeoversByShip: Record<string, Changeover[]>;
  legsByPanel: Record<string, Leg[]>;
  changeoversByPanel: Record<string, Changeover[]>;
  /** The date window the last `/plot` fetch used (string start/end). */
  currentRange: { start: string; end: string } | null;
  /** The x-extent of the drawn data, for drag-zoom no-op detection. */
  dataRange: XExtent | null;
  zoomTimer: ReturnType<typeof setTimeout> | null;
  lastTtIdx: number | null;

  /** Leg-search cache - lazily fetched once per ship. */
  allLegs: Leg[] | null;
  allLegsShipId: string | null;
  allLegsPromise: Promise<Leg[]> | null;
  legSearchHighlight: number;

  /** Rolling ms-per-row samples for the loading ETA. */
  durations: number[];

  /** DOM nodes owned by `<plot-chart>`. */
  rootEl: HTMLElement | null;
  /** Grid view's panel host, a sibling of `rootEl`. */
  gridEl: HTMLElement | null;
  tooltipEl: HTMLElement | null;
  containerEl: HTMLElement | null;
}

export const runtime: PlotRuntime = {
  instance: null,
  gridInstances: [],
  gridPanelCount: 0,
  isFetching: false,
  inRebuild: false,
  liveNowS: null,
  pendingReplot: false,
  resizeObs: null,
  gridResizeObs: null,
  lastData: null,
  lastByShip: null,
  liveSlice: null,
  lastByPeriod: null,
  periodBaseResults: null,
  comparisonGeneration: 0,
  periodViewWindow: null,
  retryPeriodPanel: null,
  lastLegs: null,
  lastChangeovers: null,
  legsByShip: {},
  changeoversByShip: {},
  legsByPanel: {},
  changeoversByPanel: {},
  currentRange: null,
  dataRange: null,
  zoomTimer: null,
  lastTtIdx: null,
  allLegs: null,
  allLegsShipId: null,
  allLegsPromise: null,
  legSearchHighlight: -1,
  durations: [],
  rootEl: null,
  gridEl: null,
  tooltipEl: null,
  containerEl: null,
};

/** `<plot-chart>` registers (or clears) its chart DOM nodes here. */
export function attachDom(
  nodes: {
    root: HTMLElement;
    grid: HTMLElement;
    tooltip: HTMLElement;
    container: HTMLElement;
  } | null,
): void {
  runtime.rootEl = nodes?.root ?? null;
  runtime.gridEl = nodes?.grid ?? null;
  runtime.tooltipEl = nodes?.tooltip ?? null;
  runtime.containerEl = nodes?.container ?? null;
}

/**
 * Show or hide every plotted series at once.
 *
 * uPlot already toggles one series per legend click, but a multi-ship overlay
 * can be thirty rows deep - so "hide everything, then click back the one you
 * want" is the fast path to isolating a line.
 *
 * `target` narrows it to one chart. The controls live in each legend now, so a
 * grid panel's buttons pass their own instance and toggle that panel alone;
 * without one, every panel is driven together as before.
 *
 * Index 0 is uPlot's x series and is never a legend row. Fit overlays are
 * included deliberately: a fit with its base series hidden is noise.
 *
 * Batched so each chart redraws once rather than once per series.
 */
export function setAllSeriesVisible(visible: boolean, target?: uPlot): void {
  const targets = target
    ? [target]
    : runtime.gridInstances.length
      ? runtime.gridInstances
      : runtime.instance
        ? [runtime.instance]
        : [];
  for (const u of targets) {
    u.batch(() => {
      for (let i = 1; i < u.series.length; i++) u.setSeries(i, { show: visible });
    });
  }
}

/** Tear down every grid panel and empty its host. */
export function destroyGrid(): void {
  for (const u of runtime.gridInstances) u.destroy();
  runtime.gridInstances = [];
  runtime.gridPanelCount = 0;
  if (runtime.gridEl) {
    runtime.gridEl.innerHTML = '';
    runtime.gridEl.classList.remove('period-chart-grid');
  }
}

/** The plotted x range, used for drag-zoom no-op detection (blob computeXExtent). */
export function computeXExtent(data: AnyPlotResponse | null): XExtent | null {
  if (!data) return null;
  if (data.plot_type === 'histogram') {
    const edges = data.edges;
    if (!edges || edges.length < 2) return null;
    return { min: edges[0]!, max: edges[edges.length - 1]! };
  }
  // Map has no x axis - drag-zoom doesn't apply.
  // Map has no x axis; a heatmap's is a grid of edges and a correlation
  // matrix's is a list of signal names - neither is a range to zoom into.
  if (
    data.plot_type === 'map' ||
    data.plot_type === 'heatmap' ||
    data.plot_type === 'correlation' ||
    data.plot_type === 'pie' ||
    data.plot_type === 'box' ||
    data.plot_type === 'number' ||
    data.plot_type === 'gauge' ||
    data.plot_type === 'sankey' ||
    data.plot_type === 'table'
  ) {
    return null;
  }
  const xs = data.x;
  if (!xs || xs.length < 2) return null;
  return { min: xs[0]!, max: xs[xs.length - 1]! };
}

export function resetZoomState(): void {
  runtime.dataRange = null;
  runtime.periodViewWindow = null;
  if (runtime.zoomTimer) {
    clearTimeout(runtime.zoomTimer);
    runtime.zoomTimer = null;
  }
  plotXWindow.set(null);
}

export function resetPlotData(): void {
  if (runtime.instance) {
    runtime.instance.destroy();
    runtime.instance = null;
  }
  destroyGrid();
  runtime.lastData = null;
  runtime.lastByShip = null;
  runtime.lastByPeriod = null;
  runtime.periodBaseResults = null;
  runtime.comparisonGeneration++;
  runtime.lastLegs = null;
  runtime.lastChangeovers = null;
  runtime.legsByShip = {};
  runtime.changeoversByShip = {};
  runtime.legsByPanel = {};
  runtime.changeoversByPanel = {};
  runtime.currentRange = null;
  runtime.lastTtIdx = null;
  resetZoomState();
  changeoverLegend.set(null);
}

/** Tear the chart down to the empty state (blob `destroyChart`). */
export function destroyChart(): void {
  resetPlotData();
  if (runtime.rootEl) runtime.rootEl.innerHTML = '';
  chartReady.set(false);
  chartEmpty.set(DEFAULT_EMPTY);
}

/* ── Loading ETA (rolling ms/row) ───────────────────────── */

export function recordPlotTiming(rowCount: number | undefined, elapsedMs: number): void {
  if (!rowCount || !Number.isFinite(elapsedMs) || elapsedMs <= 0) return;
  runtime.durations.push(elapsedMs / rowCount);
  if (runtime.durations.length > 10) runtime.durations.shift();
}

export function estimatePlotSeconds(): number | null {
  const rowCount = lastEstimate.value?.row_count;
  if (!rowCount || rowCount <= 0) return null;
  // Conservative prior (~80k rows/sec) until we have rolling samples.
  const msPerRow = runtime.durations.length
    ? runtime.durations.reduce((a, b) => a + b, 0) / runtime.durations.length
    : 1 / 80;
  return Math.max(1, Math.round((rowCount * msPerRow) / 1000));
}
